import { Component,NgZone, EventEmitter, AfterViewInit, Input, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, HostBinding, OnChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PushNotifications, Token, ActionPerformed, PushNotificationSchema } from '@capacitor/push-notifications';
import { ConfigService } from 'src/app/provider/config.service';
import { distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { h } from 'ionicons/dist/types/stencil-public-runtime';
import moment from 'moment';
import { ThemeService } from 'src/app/services/theme.service';
import { AuthService } from 'src/app/services/auth.service';
import { RouterStateTrackerService } from 'src/app/services/router-state-tracker.service';
import { Router, NavigationExtras } from '@angular/router';
import { first } from 'rxjs/operators';
import { NativeBiometric, AvailableResult } from 'capacitor-native-biometric'; // adjust import if needed
import { OtpVerificationPage } from '../../../app/pages/otp-verification/otp-verification.page';
import { environment } from 'src/environments/environment';
import { endpoints } from 'src/app/constant/endpoints';

// Services you injected before (make sure to import correctly)
import { ApiService } from 'src/app/provider/api.service';
import { DataService } from 'src/app/services/data.service';
import { ModalController } from '@ionic/angular';
import { Device } from '@awesome-cordova-plugins/device/ngx';

@Component({
  selector: 'signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss'],
  standalone: false,
})
export class SignInComponent {
  loginForm = {
    phone: '',
    password: '',
    device_token: '',
  };

  // UI state
  showPasswordFlag = false;
  passwordInputType: 'password' | 'text' = 'password';
  dialCode = '965'; // example default
  userPhoneAllowedDigit = 8;
  loginState = '';
  cardOpacity = 1;

  // external values
  pushToken: string = ''; // inject actual push token logic
  storeData: any;
  country_iso_code: any;

  loading: boolean = false;
  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private configService: ConfigService,
    private dataService: DataService,
    private modalCtrl: ModalController,
    private ngZone: NgZone,
    private router: Router,
     private device: Device,
         private routerStateTrackerService: RouterStateTrackerService,

  ) {
    // console.log(this.device.platform)
    if (this.device.platform != null ){
      this.setupPushNotifications();
    }
        if (environment.IS_INTERNAL == true) {
          this.loginForm.phone = '60637521';
          this.loginForm.password = '009009009dm';
        } else {
          this.loginForm.phone = '';
          this.loginForm.password = '';
        }
            const config = this.dataService.getCachedData('configurations');
      // console.log(config, 'config');
      this.storeData = config.organization;
        this.userPhoneAllowedDigit = this.storeData.allowed_digit_for_phone || 10;
        this.country_iso_code = this.storeData.dialing_code || '965';
        
  }

  // --- Input handling
  onPhoneInput(event: Event) {
    const input = (event.target as HTMLInputElement).value.replace(/\D/g, '');
    this.loginForm.phone = input.slice(0, this.userPhoneAllowedDigit);
  }

  togglePasswordVisibility(): void {
    this.showPasswordFlag = !this.showPasswordFlag;
    this.passwordInputType = this.showPasswordFlag ? 'text' : 'password';
  }
  setupPushNotifications(): void {
    PushNotifications.requestPermissions().then((result) => {
      if (result.receive === 'granted') {
        PushNotifications.register();
        // this.getFCMToken();
      }
    });

    PushNotifications.addListener('registration', (token: Token) => {
      this.pushToken = token.value;
    });

    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('Push registration error:', error);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {});

    PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {});
  }
  // --- Biometric
  async manualBiometricLogin(): Promise<void> {
    this.loading = true;
    try {
      const loginData = await this.configService.getStorage('loginData');
      if (loginData) {
        await this.performBiometricVerification(loginData);
      } else {
        this.loading = false;
        this.toastMessage('no_login_data_found_for_biometric');
      }
    } catch (error) {
      console.error('Error fetching login data for biometric:', error);
      this.loading = false;
      this.toastMessage('error_accessing_biometric_data');
    }
  }

  private async performBiometricVerification(loginData: any): Promise<void> {
    try {
      this.loading = true;
      const result: AvailableResult = await NativeBiometric.isAvailable();
      if (result.isAvailable) {
        await NativeBiometric.verifyIdentity({
          reason: `Login to ${this.configService.currentSettings.appName}`,
          title: `Login to ${this.configService.currentSettings.appName}`,
          subtitle: 'Verify identity',
          description: `Unlock your device to use ${this.configService.currentSettings.appName}`,
        });
        this.loading = false;
        this.cardOpacity = 0;
        this.loginForm.phone = loginData.phone;
        this.loginForm.password = loginData.password;
        await this.onSubmitLogin();
      }
    } catch (error) {
      console.error('Biometric verification failed:', error);
      this.loading = false;
      this.toastMessage('biometric_verification_failed');
    }
  }

  // --- Login
  async onSubmitLogin(): Promise<void> {
    if (!this.loginForm.phone || !this.loginForm.password) {
      this.toastMessage('phone_and_password_required');
      return;
    }
    this.loading = true;
    this.loginForm.device_token = this.pushToken;

    try {
      const response = await this.apiService.post(endpoints.login, this.loginForm).pipe(first()).toPromise();

      if (response?.success) {
        this.cardOpacity = 1;

        const storageLoginData = {
          phone: Number(this.loginForm.phone),
          password: this.loginForm.password,
        };

        this.authService.setAuthToken(response.data.customer.session_token);
        this.authService.setAuthUser(response.data.customer);

        if (!response.data.customer.is_verified) {
          this.authService.setAuthVerify('false');
          this.loading = false;
          const otpModal = await this.modalCtrl.create({
            component: OtpVerificationPage,
            componentProps: {
              phone: this.loginForm.phone,
              email: '',
              country_iso_code: this.country_iso_code,
              otp_sent: false,
            },
          });
          await otpModal.present();

          const { data } = await otpModal.onDidDismiss();
          if (data) {
            this.authService.setAuthVerify('true');
            await this.configService.setStorage('loginData', storageLoginData);
            await this.updateAppSettingsWithUser();
            this.loading = false;
            this.navigateToPage('/accounts');
          }
        } else {
          this.authService.setAuthVerify('true');
          await this.configService.setStorage('loginData', storageLoginData);
          await this.updateAppSettingsWithUser();
          this.loading = false;
          this.navigateToPage('/accounts');
        }
      } else {
        this.loading = false;
        this.toastMessage(response?.message || 'login_failed');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      const errorMessage = err.error?.message || err.error?.error || 'an_unexpected_error_occurred';
      this.toastMessage(errorMessage);
      this.loading = false;
    }
  }

  async updateAppSettingsWithUser(): Promise<void> {
    const authenticatedUser = this.authService.getAuthUser();
    await this.configService.updateAppSettings({
      currentUser: authenticatedUser || null,
    });

  }

  async resetPassword(): Promise<void> {
    const phoneNumber = this.loginForm.phone;
    if (!phoneNumber) {
      this.toastMessage('phone_number_is_required');
      return;
    }
this.loading = true;
    try {
      const response = await this.apiService.post(endpoints.resetPassword, { phone: phoneNumber }).pipe(first()).toPromise();

      if (response?.success) {
        this.loading = false;
        this.toastMessage(response.message);
        this.ngZone.run(() => {
          const navigationExtras: NavigationExtras = {
            queryParams: {
              phoneNo: phoneNumber,
              country_iso_code: this.country_iso_code,
              redirectTo: environment.LOGIN_PAGE,
              historyRoute: environment.LOGIN_PAGE,
            },
          };
          this.router.navigate(['/reset-password'], navigationExtras);
        });
      } else {
        this.loading = false;
        this.toastMessage(response?.message || 'password_reset_failed');
      }
    } catch (err: any) {
      console.error('Password reset error:', err);
      this.loading = false;
      this.toastMessage(err.error?.message || 'an_unexpected_error_occurred');
    }
  }

  // --- Utils
  // private navigateToPage(path: string, extras?: any): void {
  //   this.router.navigate([path], { queryParams: extras || {} });
  // }
  navigateToPage(page: string, data: any = { test: 'From Onb' }): void {
    const main = this.routerStateTrackerService.combinedData;
    this.routerStateTrackerService.navigateWithPreExitAnimation(page, data);
    // this.router.navigate([page], navigationExtras);
  }
  private toastMessage(key: string): void {
    // this.toast.show(key); // adjust if your toast service uses a different method
  }
}

// @Component({
//   selector: 'signin',
//   templateUrl: './signin.component.html',
//   styleUrls: ['./signin.component.scss'],
  
// })
// export class SignInComponent implements AfterViewInit, OnChanges {
//   @Input() item: any;
//   @Input() type: string = 'pending';
//   @Input() remaining?: any;
//   @Input() macros: any = {};
//   @Output() quantityChangeRequest = new EventEmitter<{
//     itemId: number;
//     isPlusClick: boolean;
//   }>();
//   @Output() freeze = new EventEmitter<any>();
//   @Output() upgrade = new EventEmitter<{ sub: any }>();
//   @Output() renew = new EventEmitter<{ route: any }>();
//   @Output() pay = new EventEmitter<{ sub: any }>();
//   @Output() cancel = new EventEmitter<{ sub: any }>();

//   @HostBinding('style.--width-calorie')
//   get calorieW(): string | null {
//     if (!this.calorieWidth) {
//       this.widthCalc();
//     }
//     return this.calorieWidth ? `${this.calorieWidth}vw` : '0vw';
//   }
//   @HostBinding('style.--width-protein')
//   get proteinW(): string | null {
//     if (!this.proteinWidth) {
//       this.widthCalc();
//     }
//     return this.proteinWidth ? `${this.proteinWidth}vw` : '0vw';
//   }
//   @HostBinding('style.--width-carb')
//   get carbW(): string | null {
//     if (!this.carbWidth) {
//       this.widthCalc();
//     }
//     return this.carbWidth ? `${this.carbWidth}vw` : '0vw';
//   }
//   @HostBinding('style.--width-fat')
//   get fatW(): string | null {
//     if (!this.fatWidth) {
//       this.widthCalc();
//     }

//     return this.fatWidth ? `${this.fatWidth}vw` : '0vw';
//   }

//   proteinWidth: any;
//   carbWidth: any;
//   fatWidth: any;
//   calorieWidth: any;
//   todayDate: Date;
//   public progressVal: number = 0; // Initialize the property

//   storeData: any = {};
//   appL: string = 'en';
//   public settings: any;
//   private appSettingsSubscription: Subscription;

//   private destroy$ = new Subject<void>(); // Added for managing subscriptions
//   remainingDays: number;
//   customer: any = {};
//   profileImage: any;
//   profileName: any;
//   profileNumber: any;
//   progressBarCal: boolean = false;
//   progressBarCarb: boolean = false;
//   progressBarFat: boolean = false;

//   progressBarProt: boolean = false;

//   constructor(
//     private translate: TranslateService,
//     private authService: AuthService,
//     private configService: ConfigService,
//     private cdRef: ChangeDetectorRef, // Keep ChangeDetectorRef if needed for internal async ops or special cases
//     private theme: ThemeService, // Assuming you have a ThemeService for theme management
//     private routerStateTrackerService: RouterStateTrackerService,
//   ) {
//     this.appSettingsSubscription = this.configService.appSettings$.subscribe((currentSettings) => {
//       this.settings = currentSettings;
//       this.translate.setDefaultLang(this.settings.appLanguage);
//       this.translate.use(this.settings.appLanguage);
//       this.appL = this.settings.appLanguage;
//     });
//   }
//   ngOnChanges() {
//     this.widthCalc();
//     this.getD();
//     this.todayDate = new Date();
//   }
//   widthCalc() {
//     let max = this.item.detail.maximum_calories;
//     let total = this.macros.calories;
//     let variance = this.macros.calories > max ? true : false;
//     let offset;
//     if (variance === false) {
//       offset = Math.ceil((this.macros.calories / max) * 100) % 26;
//       this.progressBarCal = false;
//       this.calorieWidth = offset;
//     } else {
//       offset = 26;
//       this.progressBarCal = true;
//       this.calorieWidth = 40;
//     }
//     // let total = this.macros.protein + this.macros.fats + this.macros.carbs;

//     this.fatWidth = Math.ceil(((this.macros.fats * 9) / total) * 100) % offset;
//     if (this.fatWidth >= offset) {
//       this.progressBarFat = true;
//     } else {
//       this.progressBarFat = false;
//     }

//     this.carbWidth = Math.ceil((((this.macros.carbs * 4) / total) * 100) % offset);
//     if (this.carbWidth >= offset) {
//       this.progressBarCarb = true;
//     } else {
//       this.progressBarCarb = false;
//     }
//     this.proteinWidth = Math.ceil((((this.macros.protein * 4) / total) * 100) % offset);
//     if (this.proteinWidth >= offset) {
//       this.progressBarProt = true;
//     } else {
//       this.progressBarProt = false;
//     }
//   }
//   getD() {
//     this.authService.currentUser$
//       .pipe(
//         distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
//         tap((user) => {
//           this.customer = user;
//         }),
//       )
//       .subscribe();
//     this.profileImage = this.customer.picture;
//     this.profileName = this.customer.name;
//     this.profileNumber = this.customer.phone;
//   }
//   calculateProgress(item: any): number {
//     const startDate = moment(item.start_date);
//     const endDate = moment(item.end_date);
//     const currentDate = moment();

//     // 1. Calculate the total duration of the period in days
//     const totalDurationDays = endDate.diff(startDate, 'days');

//     // Handle cases where totalDurationDays might be zero or negative
//     if (totalDurationDays <= 0) {
//       // If the end date is on or before the start date, or they are the same,
//       // it's not a valid duration for this calculation.
//       // Return 0 for invalid/no duration.
//       return 0;
//     }

//     // 2. Calculate the elapsed duration from the start date to the current date
//     //    If current date is before start date, elapsedDays will be negative or zero.
//     const elapsedDays = currentDate.diff(startDate, 'days');

//     // 3. Calculate the percentage
//     let percentage: number;

//     if (currentDate.isSameOrAfter(endDate)) {
//       // If current date is past or on the end date, progress is 100%
//       percentage = 100;
//       // this.remainingDays = 0
//       this.remainingDays = this.remaining;
//     } else if (currentDate.isSameOrBefore(startDate)) {
//       // If current date is on or before the start date, progress is 0%
//       percentage = 0;
//       this.remainingDays = this.remaining;
//       // this.remainingDays = this.remaining; // All days are remaining
//       // this.remainingDays = totalDurationDays;
//     } else {
//       // Otherwise, calculate based on elapsed time relative to total duration
//       // this.remainingDays = endDate.diff(currentDate, 'days');
//       this.remainingDays = this.remaining;
//       percentage = (elapsedDays / totalDurationDays) * 100;
//       // Ensure percentage is between 0 and 100
//       percentage = Math.max(0, Math.min(100, percentage));
//     }

//     // You might want to round or fix the number of decimal places
//     return parseFloat(percentage.toFixed(2)); // Rounds to 2 decimal places
//   }

//   async ngAfterViewInit() {
//     this.storeData = await this.configService.configurationsSync();
//     this.remainingDays = this.remaining;

//     this.progressVal = this.calculateProgress(this.item);
//     this.getD();
//     this.cdRef.detectChanges();

//     // Removed this.cdRef.detectChanges(); - With OnPush, inputs are detected automatically.
//   }

//   ngOnDestroy() {
//     this.destroy$.next(); // Emit to signal component destruction
//     this.destroy$.complete(); // Complete the subject
//   }

//   upgradeClick() {
//     this.upgrade.emit(this.item);
//     this.cdRef.detectChanges();
//   }
//   payClick() {
//     this.pay.emit(this.item.id);
//     this.cdRef.detectChanges();
//   }
//   cancelClick() {
//     this.cancel.emit(this.item.id);
//     this.cdRef.detectChanges();
//   }

//   freezeClick() {
//     this.freeze.emit();
//     this.cdRef.detectChanges();
//   }

//   onMenuDetailPage() {
//     this.renew.emit(this.item);
//     this.theme.setTheme('dark3');
//   }

//   handleClick1() {
//     if (this.type !== 'pending') {
//       this.freezeClick();
//       // this.theme.setTheme('dark');
//       this.cdRef.detectChanges();
//       //  window.location.reload();
//     } else {
//       this.cancelClick();
//       // this.theme.setTheme('dark2');
//       this.cdRef.detectChanges();
//       //  window.location.reload();
//     }
//   }
//   handleClick2() {
//     if (this.type == 'pending') {
//       this.upgradeClick();
//       // this.theme.setTheme('light');
//       this.cdRef.detectChanges();
//       //  window.location.reload();
//     } else {
//       this.payClick();
//       // this.theme.setTheme('light2');
//       this.cdRef.detectChanges();
//       //  window.location.reload();
//     }
//   }
//   renewClick() {
//     // this.theme.setTheme('dark3');
//     this.renew.emit();
//     this.cdRef.detectChanges();
//     // this.cdRef.detectChanges();
//     //  window.location.reload();
//   }
//   onSelectFoodMenu() {
//     if (this.item.quantity == 0) {
//       this.freezeClick();
//     } else {
//       this.upgradeClick();
//     }
//   }

//   getShortTitle = (data: string): string => {
//     if (!data) return '';
//     if (data.length > 39) {
//       return data.substring(0, 39) + '...';
//     }
//     return data;
//   };
//   navigateToPage(page: string, data: any = { test: 'from mycard' }): void {
//     const main = this.routerStateTrackerService.combinedData;

//     this.routerStateTrackerService.navigateWithPreExitAnimation(page, data);
//     // this.router.navigate([page], navigationExtras);
//   }
//   getShortDesc = (data: string): string => {
//     if (!data) return '';
//     if (data.length > 90) {
//       return data.substring(0, 90) + '...';
//     }
//     return data;
//   };

//   getParseFloat = (num: any): number => {
//     return Number(parseFloat(num).toFixed(0));
//   };
// }
